import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../helper/api.interceptor";
import Swal from "sweetalert2";

const Farmers = () => {
    const [farmers, setFarmers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const { id } = useParams();

    const fetchFarmerFarmDataInit = async () => {
        try {
            const data = await api.getAllCustomers();
            const sortedData = (data || []).sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setFarmers(sortedData);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    useEffect(() => {
        fetchFarmerFarmDataInit();
    }, []);

    const filteredFarmers = farmers.filter((farm) => {
        const query = searchQuery.trim().toLowerCase();
        const statusMatch =
            filterStatus === "All" ||
            (farm?.documents?.status || "") === filterStatus;
        return (
            statusMatch &&
            (farm?.personal_details?.user_name || "")
                .toLowerCase()
                .includes(query)
        );
    });

    const totalPages = Math.ceil(filteredFarmers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedFarmers = filteredFarmers.slice(
        startIndex,
        startIndex + itemsPerPage
    );

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
    };

    const handlePrev = () => {
        if (currentPage > 1) setCurrentPage((prev) => prev - 1);
    };

    const handleDeleteClick = async (id, user_id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const data = await api.fetchFarmDelete(id, user_id);
                if (data && data.status_code === 200) {
                    Swal.fire("Deleted!", "The farmer has been deleted.", "success");
                    fetchFarmerFarmDataInit();
                } else {
                    Swal.fire("Error!", "Failed to delete the farmer.", "error");
                }
            } catch (error) {
                Swal.fire("Error!", error.message, "error");
            }
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <h6 className="fw-semibold mb-0">Customers List</h6>
                <ul className="d-flex align-items-center gap-2">
                    <li className="fw-medium">
                        <Link
                            to="/"
                            className="d-flex align-items-center gap-1 hover-text-primary"
                        >
                            <Icon
                                icon="solar:home-smile-angle-outline"
                                className="icon text-lg"
                            />
                            Dashboard
                        </Link>
                    </li>
                    <li> - </li>
                    <li className="fw-medium">Customers</li>
                </ul>
            </div>

            <div className="card">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value={filteredFarmers.length}>All</option>
                        </select>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                        <div className="icon-field">
                            <input
                                type="text"
                                className="form-control form-control-sm w-auto"
                                placeholder="Search by Name"
                                onChange={handleSearchChange}
                            />
                            <span className="icon">
                                <Icon icon="ion:search-outline" />
                            </span>
                        </div>
                    </div>
                </div>

                <div className="card-body" style={{ overflowX: "auto" }}>
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th>S.L</th>
                                <th>Customer's Name</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                                <th>Booking Date</th>
                                <th>Payment Mode</th>
                                <th>Project Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedFarmers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center">
                                        No Customer found.
                                    </td>
                                </tr>
                            ) : (
                                paginatedFarmers.map((farmer, index) => (
                                    <tr key={farmer._id}>
                                        <td>{startIndex + index + 1}</td>
                                        <td>
                                            {farmer?.personal_details?.user_name ||
                                                "No Name Available"}
                                        </td>
                                        <td>
                                            {farmer?.personal_details?.email ||
                                                "No Email Available"}
                                        </td>
                                        <td>
                                            {farmer?.phone_number ||
                                                "No Phone Provided"}
                                        </td>
                                        <td>
                                            {farmer?.created_at
                                                ? new Date(
                                                    farmer.created_at
                                                ).toLocaleDateString()
                                                : "No Date"}
                                        </td>
                                        <td>
                                            {farmer?.payment?.mode ||
                                                "Not Available"}
                                        </td>
                                        <td>
                                            {farmer?.documents?.status ||
                                                farmer?.order_summary?.status ||
                                                "Pending"}
                                        </td>
                                        <td>
                                            <Link
                                                to={`/customer-order-details/${encodeURIComponent(
                                                    farmer?.phone_number
                                                )}`}
                                                className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                            >
                                                <Icon icon="iconamoon:eye-light" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className="d-flex justify-content-end align-items-center gap-4 mt-3">
                        <button
                            className="btn btn-sm"
                            onClick={handlePrev}
                            disabled={currentPage === 1}
                            style={{
                                backgroundColor: "#ff9800",
                                color: "white",
                                border: "none",
                                padding: "10px 40px",
                                height: "auto",
                            }}
                        >
                            Prev
                        </button>

                        <span>
                            Page {currentPage} of {totalPages || 1}
                        </span>

                        <button
                            className="btn btn-sm"
                            onClick={handleNext}
                            disabled={currentPage === totalPages || totalPages === 0}
                            style={{
                                backgroundColor: "#ff9800",
                                color: "white",
                                border: "none",
                                padding: "10px 40px",
                                height: "auto",
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Farmers;
